/**
 * THE GUIDE ENGINE — pure functions over the saved guide state, the game's events and its world summary. No React,
 * no DOM, no timers: everything here is unit-tested (guide.test.ts), and the UI (src/ui/guide/) only renders what
 * these functions decide.
 *
 *   state  — what the player has done (step, chapter and goal ids) and which tips they have seen. Saved as
 *            `SaveData.guide`; ids, NEVER positions, so adding, removing or reordering content cannot move a
 *            player onto the wrong step.
 *   event  — something the game announced (events.ts).
 *   world  — a small read-only summary of the game (content.ts GuideWorld), for "already done" checks.
 *
 * Steps finish on events, or silently when the world already shows them done (`alreadyDone`) — that sweep runs
 * after every event and every world change, to a fixpoint, which is what lets a player wander ahead.
 */
import {
  CHAPTERS, GOALS, DISCOVERIES, type GuideChapter, type GuideGoal, type GuideDiscovery, type GuideStep,
  type GuideWorld, type EventMatch,
} from "./content";
import type { GuideEvent } from "./events";
import { NO_HELPERS, type GuideHelpers } from "./helpers";

export const GUIDE_VERSION = 1;

export interface GuideState {
  v: number;
  /** finished steps (`<id>`), chapters (`ch:<id>`), goals (`goal.*`) and paid rewards (`reward:<id>`) */
  done: string[];
  /** Discoveries already shown */
  seen: string[];
  /** "Show tips" off: Discoveries are kept for the Journal but never pop up */
  tipsOff: boolean;
  /** when this player STARTED the guide fresh (ms) — absent for a veteran migrated past it. It is what puts a save in
   *  the admin's tutorial funnel (server/guideFunnel.js): only players who began the tutorial can drop out of it. */
  since?: number;
}

export const chapterKey = (c: GuideChapter) => `ch:${c.id}`;
export const rewardKey = (c: GuideChapter) => `reward:${c.id}`;
/** Where a chapter was skipped (`skip:<chapter>:<step>`) — the funnel's "gave up here". */
export const skipKey = (c: GuideChapter, stepId: string) => `skip:${c.id}:${stepId}`;
/** Did this player skip (some of) the tutorial rather than play it through? (The Journal says so.) */
export function tutorialSkipped(s: GuideState): boolean { return s.done.some((id) => id.startsWith("skip:")); }

/** A brand-new player. */
export function freshGuide(now: number = Date.now()): GuideState {
  return { v: GUIDE_VERSION, done: [], seen: [], tipsOff: false, since: now };
}

/** A player who was here before this tutorial existed: Chapter 1 (and its reward) are behind them. */
export function veteranGuide(): GuideState {
  const done: string[] = [];
  for (const c of CHAPTERS) done.push(chapterKey(c), rewardKey(c), ...c.steps.map((s) => s.id));
  return { v: GUIDE_VERSION, done, seen: [], tipsOff: false };
}

/** The old tutorial had four steps (0–3); 1000 was "finished". Fixed forever — it describes saves already written. */
const LEGACY_TUTORIAL_STEPS = 4;
const MAX_IDS = 400;
const ids = (v: unknown): string[] => (Array.isArray(v)
  ? [...new Set(v.filter((x): x is string => typeof x === "string" && x.length > 0 && x.length <= 64))].slice(0, MAX_IDS) : []);

/**
 * The saved guide, cleaned — or, for a save written before it existed, derived from the old `onboardingStep`:
 * a value inside the old four-step tutorial (0–3) was a new player, who starts Chapter 1 fresh; anything else
 * (finished, or a save older than the old tutorial) is a veteran.
 */
export function coerceGuide(raw: unknown, legacyOnboardingStep?: unknown): GuideState {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const since = typeof o.since === "number" && Number.isFinite(o.since) && o.since > 0 ? o.since : undefined;
    const state: GuideState = { v: GUIDE_VERSION, done: ids(o.done), seen: ids(o.seen), tipsOff: o.tipsOff === true, ...(since ? { since } : {}) };
    // NO `since` = a VETERAN (migrated past the tutorial, never started it fresh): every chapter is behind them,
    // including any chapter added after they were migrated — Chapter 2 must not ambush a level-40 garden.
    return since ? state : markDone(state, ...veteranGuide().done);
  }
  const step = typeof legacyOnboardingStep === "number" && Number.isFinite(legacyOnboardingStep) ? Math.floor(legacyOnboardingStep) : -1;
  return step >= 0 && step < LEGACY_TUTORIAL_STEPS ? freshGuide() : veteranGuide();
}

const has = (s: GuideState, id: string) => s.done.includes(id);
const matches = (m: EventMatch, e: GuideEvent) => m.event === e.type && (!m.where || m.where(e));

export function chapterDone(s: GuideState, c: GuideChapter): boolean { return has(s, chapterKey(c)); }
/** The chapter being played now, or null when every chapter is behind the player. */
export function activeChapter(s: GuideState): GuideChapter | null {
  return CHAPTERS.find((c) => !chapterDone(s, c)) ?? null;
}
/** Is the guided first session over (finished or skipped)? Goals and Discoveries wait for it. */
export function firstSessionDone(s: GuideState): boolean { return CHAPTERS.every((c) => chapterDone(s, c)); }

const ready = (s: GuideState, st: GuideStep) => !has(s, st.id) && (st.after ?? []).every((a) => has(s, a));

/** Steps of the active chapter that can be done right now. */
export function availableSteps(s: GuideState, w: GuideWorld): GuideStep[] {
  const c = activeChapter(s);
  if (!c) return [];
  return c.steps.filter((st) => ready(s, st) && (!st.showWhen || st.showWhen(w)));
}

/** The one step to show: a BUSY step (`busy` = its id, see GuideStep.busy) while it is still available, else the
 *  highest priority, then the earliest in the chapter. */
export function currentStep(s: GuideState, w: GuideWorld, busy: string | null = null): GuideStep | null {
  const avail = availableSteps(s, w);
  const held = busy ? avail.find((st) => st.id === busy) : undefined;
  if (held) return held;
  let best: GuideStep | null = null;
  for (const st of avail) if (!best || (st.priority ?? 1) > (best.priority ?? 1)) best = st;
  return best;
}

/** The step a BUSY step is holding off the screen — what would show without it — or null when nothing is waiting. */
export function heldBack(s: GuideState, w: GuideWorld, busy: string | null): GuideStep | null {
  if (!busy) return null;
  const shown = currentStep(s, w, busy), free = currentStep(s, w);
  return shown?.id === busy && free && free.id !== busy ? free : null;
}

/** Which step is BUSY after an event. `cur` is the step that was on screen when the event arrived (with the busy
 *  hold applied), `s` the state after it. A step goes busy on one of its `on` events and stays busy until one of its
 *  `off` events, or until it is no longer available (done, or the chapter over). */
export function nextBusy(busy: string | null, cur: GuideStep | null, e: GuideEvent, s: GuideState, w: GuideWorld): string | null {
  if (!cur?.busy || (busy && busy !== cur.id)) return null;
  const holds = busy ? !cur.busy.off.includes(e.type) : cur.busy.on.includes(e.type);
  return holds && availableSteps(s, w).some((st) => st.id === cur.id) ? cur.id : null;
}

/** Progress of a chapter, for the bubble's "4 / 9". */
export function chapterProgress(s: GuideState, c: GuideChapter): [number, number] {
  return [c.steps.filter((st) => has(s, st.id)).length, c.steps.length];
}

export interface GuideOutcome {
  state: GuideState;
  changed: boolean;
  /** chapters that completed in this update — the UI pays the reward and shows the card */
  chaptersDone: GuideChapter[];
  goalsDone: GuideGoal[];
  /** Discoveries whose trigger fired and that were not seen yet */
  discovered: GuideDiscovery[];
}

function finish(prev: GuideState, next: GuideState, goalsDone: GuideGoal[], discovered: GuideDiscovery[]): GuideOutcome {
  const chaptersDone = CHAPTERS.filter((c) => !has(prev, chapterKey(c)) && has(next, chapterKey(c)));
  const changed = next.done.length !== prev.done.length || next.seen.length !== prev.seen.length || next.tipsOff !== prev.tipsOff;
  return { state: changed ? next : prev, changed, chaptersDone, goalsDone, discovered };
}

/** The "already done" sweep and chapter completion, to a fixpoint. */
function settle(s: GuideState, w: GuideWorld): GuideState {
  let state = s;
  for (let guard = 0; guard < 50; guard++) {
    const c = activeChapter(state);
    if (!c) return state;
    const auto = c.steps.filter((st) => ready(state, st) && st.alreadyDone?.(w));
    if (!auto.length) {
      if (c.steps.every((st) => has(state, st.id))) { state = { ...state, done: [...state.done, chapterKey(c)] }; continue; }
      return state;
    }
    state = { ...state, done: [...state.done, ...auto.map((st) => st.id)] };
  }
  return state;
}

/** Goals finished by their progress reaching the target. */
function goalsByProgress(s: GuideState, w: GuideWorld): GuideGoal[] {
  if (!firstSessionDone(s)) return [];
  return GOALS.filter((g) => !has(s, g.id) && g.progress && (() => { const [cur, max] = g.progress!(w); return cur >= max; })());
}

/** An event from the game. */
export function applyEvent(s: GuideState, e: GuideEvent, w: GuideWorld): GuideOutcome {
  let state = s;
  const c = activeChapter(state);
  if (c) {
    const hit = c.steps.filter((st) => ready(state, st) && st.doneOn.some((m) => matches(m, e)));
    if (hit.length) state = { ...state, done: [...state.done, ...hit.map((st) => st.id)] };
  }
  state = settle(state, w);
  const goalsDone: GuideGoal[] = [];
  const discovered: GuideDiscovery[] = [];
  if (firstSessionDone(state)) {
    for (const g of GOALS) if (!has(state, g.id) && g.doneOn?.some((m) => matches(m, e))) goalsDone.push(g);
    for (const g of goalsByProgress(state, w)) if (!goalsDone.includes(g)) goalsDone.push(g);
    if (goalsDone.length) state = { ...state, done: [...state.done, ...goalsDone.map((g) => g.id)] };
    for (const d of DISCOVERIES) if (d.on && !state.seen.includes(d.id) && matches(d.on, e)) discovered.push(d);
  }
  return finish(s, state, goalsDone, discovered);
}

/**
 * The world changed. `prev` is the world at the previous check (undefined on the first one after load) — a
 * Discovery's `when` fires on its false → true EDGE, so a condition that was already true when the game loaded
 * does not pop a tip at the player (unless that Discovery says `atLoad`).
 */
export function applyWorld(s: GuideState, w: GuideWorld, prev: GuideWorld | undefined): GuideOutcome {
  let state = settle(s, w);
  const met = goalsByProgress(state, w);
  if (met.length) state = { ...state, done: [...state.done, ...met.map((g) => g.id)] };
  // A goal ALREADY MET when the game loaded (no `prev`) is recorded but not celebrated: a veteran's first load
  // after the Journal arrived would otherwise toast "Goal done" for everything they did before it existed.
  const goalsDone = prev ? met : [];
  const discovered: GuideDiscovery[] = [];
  if (firstSessionDone(state)) {
    for (const d of DISCOVERIES) {
      if (!d.when || state.seen.includes(d.id) || !d.when(w)) continue;
      const was = prev ? d.when(prev) : !d.atLoad;
      if (!was) discovered.push(d);
    }
  }
  return finish(s, state, goalsDone, discovered);
}

/** Skip the WHOLE tutorial: every remaining chapter, each paid (the UI pays and shows every one in `chaptersDone`).
 *  `atStep` (the step on screen) is recorded on the chapter the player was in; later chapters at their first step. */
export function skipTutorial(s: GuideState, atStep?: string): GuideOutcome {
  let state = s;
  const chaptersDone: GuideChapter[] = [];
  for (let c = activeChapter(state), first = true; c; c = activeChapter(state), first = false) {
    const o = skipChapter(state, c, first ? atStep : c.steps[0]?.id);
    state = o.state; chaptersDone.push(...o.chaptersDone);
  }
  return { ...finish(s, state, [], []), chaptersDone };
}

/** The step's words for this world: the first matching variant, else its own. */
export function stepCopy(st: GuideStep, w: GuideWorld): { text: string; touchText?: string } {
  const v = st.variants?.find((x) => x.when(w));
  return v ? { text: v.text, touchText: v.touchText } : { text: st.text, touchText: st.touchText };
}

/** Skip a chapter: every step done (its reward is still paid — the UI sees it in `chaptersDone`). `atStep` is the step
 *  on screen when the player gave up, recorded for the funnel. */
export function skipChapter(s: GuideState, c: GuideChapter, atStep?: string): GuideOutcome {
  const add = [...c.steps.map((st) => st.id), chapterKey(c), ...(atStep ? [skipKey(c, atStep)] : [])].filter((id) => !has(s, id));
  return finish(s, { ...s, done: [...s.done, ...add] }, [], []);
}

/** Mark ids done (a paid reward, a Welcome answered…). */
export function markDone(s: GuideState, ...add: string[]): GuideState {
  const fresh = add.filter((id) => !has(s, id));
  return fresh.length ? { ...s, done: [...s.done, ...fresh] } : s;
}

export function markSeen(s: GuideState, id: string): GuideState {
  return s.seen.includes(id) ? s : { ...s, seen: [...s.seen, id] };
}

/** The Journal's goals: the first few not yet done, with their progress. */
export function journalGoals(s: GuideState, w: GuideWorld, n: number): Array<{ goal: GuideGoal; cur: number; max: number }> {
  if (!firstSessionDone(s)) return [];
  return GOALS.filter((g) => !has(s, g.id)).slice(0, n).map((goal) => {
    const [cur, max] = goal.progress ? goal.progress(w) : [0, 1];
    return { goal, cur: Math.min(cur, max), max };
  });
}

/** Should the gameplay helpers for the first session be on? (first-crop boost, a guaranteed first bite and bug) */
export function firstSessionHelpers(s: GuideState): GuideHelpers {
  const c = activeChapter(s);
  if (c?.id === "first-harvest") return { ...NO_HELPERS, boostFirstCrop: !has(s, "plant"), sureBite: !has(s, "fish") };
  if (c?.id === "make-it-yours") {
    const bug = c.steps.find((st) => st.id === "catch-bug");
    return { ...NO_HELPERS, tutorialBug: !!bug && ready(s, bug) };
  }
  return NO_HELPERS;
}
